import { EventEmitter, Logger } from '@quickplay/foundation';
import {
  AdComposedPlayer,
  BUFFERING_STATES,
  PLAYBACK_STATES,
  Player,
  SEEKING_STATES,
} from '@quickplay/player-interface';
import { createAFCPlaybackSessionManager, AFCPlaybackSession } from '@quickplay/player';
import { PlayerBuilder } from '../../PlayerBuilder';
import { AFCComposedAdPlayer } from '../../models/AFCComposedCsaiAdPlayer';

export const createComposableAFCPlaybackSession = (
  builder: PlayerBuilder,
  player: AdComposedPlayer,
  adPlayer: Player,
): AFCPlaybackSession | undefined => {
  if (!builder.isAfcEnabled) {
    return undefined;
  }

  const session = createAFCPlaybackSessionManager({
    player,
    adFrequencyControlConfiguration: builder.adFrequencyControlConfiguration,
    logger: builder.configuredLogger,
  });
  // Only the advanced CSAI player supports AFC sessions.
  (adPlayer as Partial<AFCComposedAdPlayer>)?.setAdFrequencySession?.(session);
  return session;
};

/**
 * Emits the current content-player state unless the player is still loading.
 */
export const emitContentPlayerCurrentState = (
  contentPlayer: Player,
  logger: Logger,
  eventEmitter: EventEmitter,
): void => {
  if (contentPlayer.playerState === PLAYBACK_STATES.LOADING) {
    return;
  }

  const playerState = {
    player: contentPlayer.playerState,
    seeking: SEEKING_STATES.INACTIVE,
    buffering: BUFFERING_STATES.INACTIVE,
  };

  logger.info('Player state has changed', JSON.stringify(playerState));
  eventEmitter.publish('playbackstatechanged', contentPlayer.playerState);
};

/**
 * Checks whether a player exposes the composed-player context API.
 */
export const isAdComposedPlayer = (player: Player): player is AdComposedPlayer => {
  return 'playerContext' in player;
};
